import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Lock } from "lucide-react";
import { toast } from "sonner";

export default function ProfileDialog({ open, onClose, shop, onUpdated }) {
  const [form, setForm] = useState({ firm_name: "", owner_name: "", mobile: "", address: "", city: "" });
  const [busy, setBusy] = useState(false);
  const [pwForm, setPwForm] = useState({ p1: "", p2: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [hasPw, setHasPw] = useState(false);

  useEffect(() => {
    if (open && shop) {
      setForm({
        firm_name: shop.name || "",
        owner_name: shop.owner_name || "",
        mobile: shop.mobile || "",
        address: shop.address || "",
        city: shop.city || "",
      });
      setHasPw(!!shop.has_password);
    }
  }, [open, shop]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      onUpdated(data);
      toast.success("Profile updated");
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const planEligible = shop && (!shop.valid_until || (shop.plan_active && new Date(shop.valid_until) > new Date()));

  const savePassword = async () => {
    if (pwForm.p1.length < 4) return toast.error("Password kam se kam 4 characters ka ho");
    if (pwForm.p1 !== pwForm.p2) return toast.error("Dono password same nahi hain");
    setPwBusy(true);
    try {
      await api.put("/auth/password", { password: pwForm.p1 });
      setHasPw(true);
      setPwForm({ p1: "", p2: "" });
      toast.success("Password set ho gaya — ab login me password zaroori hoga");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Password set nahi hua");
    } finally {
      setPwBusy(false);
    }
  };

  const removePassword = async () => {
    setPwBusy(true);
    try {
      await api.put("/auth/password", { password: "" });
      setHasPw(false);
      toast.success("Password hata diya — ab bina password login hoga");
    } catch {
      toast.error("Remove failed");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Profile</DialogTitle>
          <DialogDescription>You can update your Sign Up details here.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5">
            <Label>User Name</Label>
            <Input value={form.firm_name} onChange={set("firm_name")} required minLength={2} data-testid="profile-firm-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Owner Name</Label>
            <Input value={form.owner_name} onChange={set("owner_name")} data-testid="profile-owner-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile No.</Label>
            <Input type="tel" inputMode="numeric" value={form.mobile} onChange={set("mobile")} required data-testid="profile-mobile-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Address</Label>
            <Input value={form.address} onChange={set("address")} data-testid="profile-address-input" />
          </div>
          <div className="space-y-1.5">
            <Label>City</Label>
            <Input value={form.city} onChange={set("city")} data-testid="profile-city-input" />
          </div>
          {planEligible && (
            <div className="border-t border-border pt-3 space-y-2" data-testid="profile-password-section">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" /> Login Password {hasPw && <span className="text-emerald-600 text-[11px] font-semibold">(Set)</span>}
                </Label>
                {hasPw && (
                  <button type="button" onClick={removePassword} disabled={pwBusy} data-testid="profile-password-remove-btn" className="text-xs text-red-500 hover:underline">
                    Remove
                  </button>
                )}
              </div>
              <Input type="password" placeholder={hasPw ? "Naya password (change karne ke liye)" : "Naya password (min 4 characters)"} value={pwForm.p1} onChange={(e) => setPwForm({ ...pwForm, p1: e.target.value })} data-testid="profile-password-input" />
              <Input type="password" placeholder="Password dobara dalein" value={pwForm.p2} onChange={(e) => setPwForm({ ...pwForm, p2: e.target.value })} data-testid="profile-password-confirm-input" />
              <Button type="button" size="sm" variant="outline" onClick={savePassword} disabled={pwBusy} data-testid="profile-password-save-btn" className="h-8 text-xs">
                {pwBusy ? "Saving..." : hasPw ? "Change Password" : "Set Password"}
              </Button>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="profile-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5">
              <Save className="h-4 w-4" /> {busy ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
